import type { DataSourceRequestState } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, type DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { GridColumn, GridNoRecords, type GridCellProps, type GridPageChangeEvent } from "@progress/kendo-react-grid";
import { eyeIcon, trashIcon } from "@progress/kendo-svg-icons";
import { useState } from "react";
import { useDeleteCommercialFindingMutation, useListAdminCommercialFindingsQuery } from "../../api/admin/adminCommercialFindingApi";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import { useAppDispatch } from "../../app/hooks";
import AppGrid from "../../components/grid/appGrid";
import { notify } from "../../components/notify";
import { CommercialFindingCategory, CommercialFindingStatus, CommercialFindingType, type CommercialFindingDto, type CustomerDto } from "../../contracts";
import { formatDateTime, toDate } from "../../utils/dateValueConversion";

const customerRequest: DataSourceRequestState = { skip: 0, take: 1000, sort: [{ field: "name", dir: "asc" }] };
const initialSort = [{ field: "createdAt", dir: "desc" as const }, { field: "id", dir: "desc" as const }];

const CreatedCell = (props: GridCellProps): React.ReactElement => {
    const finding = props.dataItem as CommercialFindingDto;
    const date = toDate(finding.created_at);
    return <td className="k-table-td text-nowrap">{date == null ? "-" : formatDateTime(date)}</td>;
};

const FindingCell = (props: GridCellProps): React.ReactElement => {
    const finding = props.dataItem as CommercialFindingDto;
    return (
        <td className="k-table-td">
            <div className="fw-semibold text-wrap text-break">{finding.title}</div>
            <div className="small text-muted text-wrap text-break">{finding.description}</div>
        </td>
    );
};

const ClassificationCell = (props: GridCellProps): React.ReactElement => {
    const finding = props.dataItem as CommercialFindingDto;
    return (
        <td className="k-table-td">
            <div>
                {CommercialFindingType[finding.type]}
                {" "}
                ·
                {" "}
                {CommercialFindingCategory[finding.category]}
            </div>
            <div className="small text-muted">{CommercialFindingStatus[finding.status]}</div>
        </td>
    );
};

const CommercialFindingManagement: React.FC = () => {
    const dispatch = useAppDispatch();
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 50, sort: initialSort });
    const [selectedFinding, setSelectedFinding] = useState<CommercialFindingDto | null>(null);
    const [findingToDelete, setFindingToDelete] = useState<CommercialFindingDto | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const listRequest = {
        ...dataState,
        filter: selectedCustomer == null
            ? undefined
            : {
                    logic: "and" as const,
                    filters: [{ field: "customerId", operator: "eq", value: selectedCustomer.id }]
                }
    };
    const { currentData: data, isFetching, isError, refetch } = useListAdminCommercialFindingsQuery(listRequest);
    const { data: customerData, isFetching: isCustomersFetching, isError: isCustomersError } = useListCustomersQuery(customerRequest);
    const [deleteFinding, { isLoading: isDeleting }] = useDeleteCommercialFindingMutation();
    const customerLabel = (customerId: number): string => {
        const name = customerData?.data.find(customer => customer.id === customerId)?.name;
        return name == null ? `Customer #${customerId}` : `${name} (#${customerId})`;
    };
    const handleCustomerChange = (event: DropDownListChangeEvent): void => {
        const customer = event.value as CustomerDto | null;
        setSelectedCustomer(customer?.id === 0 ? null : customer);
        setDataState(previous => ({ ...previous, skip: 0 }));
    };
    const pageChange = (event: GridPageChangeEvent): void => setDataState(previous => ({ ...previous, ...event.page }));
    const handleDelete = async (): Promise<void> => {
        if (findingToDelete == null || isDeleting) {
            return;
        }
        try {
            await deleteFinding({ commercial_finding_id: findingToDelete.id, customer_id: findingToDelete.customer_id }).unwrap();
            if ((data?.data.length ?? 0) === 1 && (dataState.skip ?? 0) > 0) {
                setDataState(previous => ({ ...previous, skip: Math.max(0, (previous.skip ?? 0) - (previous.take ?? 50)) }));
            }
            setFindingToDelete(null);
            setDeleteError(null);
            dispatch(notify("Commercial finding permanently deleted.", "success"));
        } catch {
            setDeleteError("Unable to delete the finding. It may already have been removed. Refresh the list and try again.");
        }
    };
    const CustomerCell = (props: GridCellProps): React.ReactElement => (
        <td className="k-table-td">{customerLabel((props.dataItem as CommercialFindingDto).customer_id)}</td>
    );
    const ActionsCell = (props: GridCellProps): React.ReactElement => {
        const finding = props.dataItem as CommercialFindingDto;
        return (
            <td className="k-table-td">
                <div className="d-flex gap-2">
                    <Button type="button" size="small" svgIcon={eyeIcon} onClick={() => setSelectedFinding(finding)}>View</Button>
                    <Button
                        type="button"
                        size="small"
                        themeColor="error"
                        fillMode="outline"
                        svgIcon={trashIcon}
                        disabled={isDeleting || isFetching}
                        onClick={() => {
                            setDeleteError(null);
                            setFindingToDelete(finding);
                        }}
                    >
                        Delete
                    </Button>
                </div>
            </td>
        );
    };

    return (
        <div className="d-flex flex-column gap-3 p-3 h-100 min-h-0 overflow-hidden">
            <div className="row g-3 align-items-end">
                <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold" htmlFor="admin-finding-customer">Customer</label>
                    <DropDownList id="admin-finding-customer" data={customerData?.data ?? []} textField="name" dataItemKey="id" value={selectedCustomer} onChange={handleCustomerChange} disabled={isCustomersFetching || isDeleting} defaultItem={{ id: 0, name: isCustomersFetching ? "Loading customers..." : "All customers" }} />
                </div>
                <div className="col-auto">
                    <Button type="button" disabled={isFetching || isDeleting} onClick={() => void refetch()}>Refresh</Button>
                </div>
                <div className="col-auto text-muted small">Newest findings first · All statuses</div>
            </div>
            {(isError || isCustomersError) && <div className="alert alert-danger mb-0" role="alert">Unable to load findings or customers. Please try again.</div>}
            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid showLoader={isFetching || isCustomersFetching} dataItemKey="id" scrollable="scrollable" sortable={false} filterable={false} resizable={true} data={data?.data ?? []} initialState={dataState} onPageChange={pageChange} total={data?.total ?? 0} skip={dataState.skip} take={dataState.take} pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }} style={{ height: "100%" }}>
                    <GridNoRecords>{!isFetching && <span className="text-muted">{isError ? "Unable to load commercial findings." : "No commercial findings found."}</span>}</GridNoRecords>
                    <GridColumn field="id" title="ID" width="90px" />
                    <GridColumn title="Customer" width="220px" cells={{ data: CustomerCell }} />
                    <GridColumn title="Finding" width="420px" cells={{ data: FindingCell }} />
                    <GridColumn title="Type / Category / Status" width="220px" cells={{ data: ClassificationCell }} />
                    <GridColumn title="Created" width="200px" cells={{ data: CreatedCell }} />
                    <GridColumn title="Actions" width="220px" cells={{ data: ActionsCell }} />
                </AppGrid>
            </div>
            {selectedFinding != null && (
                <Dialog title={`Commercial finding #${selectedFinding.id}`} width={760} onClose={() => setSelectedFinding(null)}>
                    <div className="text-break" style={{ maxHeight: "65vh", overflowY: "auto", whiteSpace: "pre-wrap" }}>
                        <h5>{selectedFinding.title}</h5>
                        <p className="text-muted">{customerLabel(selectedFinding.customer_id)}</p>
                        <dl>
                            <dt>Description</dt>
                            <dd>{selectedFinding.description}</dd>
                            <dt>Classification</dt>
                            <dd>
                                {CommercialFindingType[selectedFinding.type]}
                                {" "}
                                ·
                                {" "}
                                {CommercialFindingCategory[selectedFinding.category]}
                                {" "}
                                ·
                                {" "}
                                {CommercialFindingStatus[selectedFinding.status]}
                            </dd>
                            <dt>
                                Impact (score:
                                {selectedFinding.impact_score ?? "-"}
                                )
                            </dt>
                            <dd>{selectedFinding.impact ?? "-"}</dd>
                            <dt>
                                Evidence (score:
                                {selectedFinding.evidence_score ?? "-"}
                                )
                            </dt>
                            <dd>{selectedFinding.evidence ?? "-"}</dd>
                            <dt>Recommended action</dt>
                            <dd>{selectedFinding.recommended_action ?? "-"}</dd>
                            <dt>Dismissed reason</dt>
                            <dd>{selectedFinding.dismissed_reason ?? "-"}</dd>
                            <dt>Scope</dt>
                            <dd>
                                Brand:
                                {selectedFinding.brand_id ?? "-"}
                                {" "}
                                · Sub-brand:
                                {selectedFinding.sub_brand_id ?? "-"}
                                {" "}
                                · Product:
                                {selectedFinding.product_id ?? "-"}
                                {" "}
                                · Retailer:
                                {selectedFinding.retailer_id ?? "-"}
                                {" "}
                                · Pack size:
                                {selectedFinding.pack_size_key ?? "-"}
                            </dd>
                        </dl>
                    </div>
                </Dialog>
            )}
            {findingToDelete != null && (
                <Dialog title="Permanently delete finding?" width={520} onClose={() => !isDeleting && setFindingToDelete(null)}>
                    <p>
                        Delete finding #
                        {findingToDelete.id}
                        {" "}
                        for
                        {customerLabel(findingToDelete.customer_id)}
                        ?
                    </p>
                    <p className="fw-semibold text-break">{findingToDelete.title}</p>
                    <p>This permanently removes the finding and cannot be undone.</p>
                    {deleteError != null && <div className="alert alert-danger" role="alert">{deleteError}</div>}
                    <DialogActionsBar>
                        <Button type="button" disabled={isDeleting} onClick={() => setFindingToDelete(null)}>Cancel</Button>
                        <Button type="button" themeColor="error" svgIcon={trashIcon} disabled={isDeleting} onClick={() => void handleDelete()}>{isDeleting ? "Deleting..." : "Delete permanently"}</Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
};

export default CommercialFindingManagement;
